import { PaginatedResponseType } from '../../Types/Response/PaginatedResponseType';
import { useInfiniteAddresses } from '../../Api/AddressEndpoints';
import { AddressListItemType, AddressType } from '../../Types/AddressType';
import { Controller, FieldValues, FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { FormWrapperLabel } from '../../Framework/Components/Forms/FormEntryWrapper/FormEntryWrapper';
import Button, { ButtonSubmit } from '../../Framework/Components/Buttons/Button';
import { ButtonSize, ToggleType } from '../../Framework/Data/Enums/Generic';
import { ColourMod, FlexGap } from '../../Framework/Data/Enums/LayoutEnums';
import ToggleSwitch from '../../Framework/Components/Forms/Toggle/ToggleSwitch';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { AddAddressModal } from '../AddAddress';
import { SendsPageDataType } from '../../Api/StandaloneSendsEndpoints';
import SendsPostageControl from './Controls/SendsPostageControl';
import { GetObjectIndexByKeyValue } from '../../Framework/Constants/Helpers';
import StackVertical from '../../Framework/Components/Wrappers/StackVertical';
import { useInfiniteUsers, UserDataType, useUser } from '../../Api/UsersEndpoints';
import CreateUserModal from './Modals/CreateUserModal';
import AlertBanner from '../../Framework/Components/AlertBanner/AlertBanner';
import ImportCsvModal from '../AddressImporter/Modals/ImportCsvModal';
import ImportVcfModal from '../AddressImporter/Modals/ImportVcfModal';
import SendsPageCreatedActionsModal from './Modals/SendsPageCreatedActionsModal';
import InfiniteDataComboBox from '../InfiniteDataComboBox';
import { InfiniteData } from '@tanstack/query-core/src/types';
import { GetItemCounts } from '../../Framework/Constants/DataHelpers';
import EmailControl from '../../Framework/Components/Forms/Email/EmailControl';
import TelephoneControl from '../../Framework/Components/Forms/Telephone/TelephoneControl';

type CreateStandaloneSendFormType = {
	saveCallback: any;
	dataUpdateCallback?: any;
	existingData?: SendsPageDataType;
	savingData?: boolean;
	saveResponse?: any;
	error?: any;
};

const CreateStandaloneSendForm = memo((props: CreateStandaloneSendFormType) => {
	const { data } = useUser();
	const canWindow = data?.permissions?.includes('Client_AllowSendsProfilesWindowEnvelopes');

	const formMethods = useForm({
		mode: 'onChange',
	});

	const [preSelectedUsers, setPreSelectedUsers] = useState<any>(
		props.existingData?.users.map((item) => item.id) ?? []
	);
	const [preSelectedAddresses, setPreSelectedAddresses] = useState<any>(
		props.existingData?.addresses.map((item) => item.id) ?? []
	);

	const {
		isLoading: usersLoading,
		data: usersData,
		error: usersError,
		dataUpdatedAt: addressDataUpdatedAt,
		fetchNextPage: fetchNextUsersPage,
		hasNextPage: hasNextUsersPage,
		isFetchingNextPage: isFetchingNextUsersPage,
	} = useInfiniteUsers();

	const {
		isLoading: addressLoading,
		data: addressData,
		error: addressError,
		dataUpdatedAt: usersDataUpdatedAt,
		fetchNextPage: fetchNextAddressPage,
		hasNextPage: hasNextAddressPage,
		isFetchingNextPage: isFetchingNextAddressPage,
	} = useInfiniteAddresses();

	const [activeSendsPage, setActiveSendsPage] = useState<any>({ isOpen: false, pageData: null });

	const [AddressOptions, setAddressOptions] = useState<any>([]);
	const [UserOptions, setUserOptions] = useState<any>([]);
	const [lookupActive, setLookupActive] = useState<boolean>(false);
	const [canCreateAddresses, setCanCreateAddresses] = useState<boolean>(
		props.existingData?.allowCreateAddress ?? false
	);

	const MapAddressOptions = (data: InfiniteData<PaginatedResponseType>) => {
		setAddressItemCounts(GetItemCounts(data));

		const newAddresses = data?.pages.map(
			(page: any) =>
				page?.data &&
				page.data.map((address: AddressListItemType, index: number) => {
					return { label: address.label, value: address.object.addressId };
				})
		);

		setAddressOptions(newAddresses.flat());

		if (hasNextAddressPage) {
			fetchNextAddressPage().then((r) => null);
		}
	};

	const MapUserOptions = (data: InfiniteData<PaginatedResponseType>) => {
		setUsersItemCounts(GetItemCounts(data));
		const newUsers = data?.pages.map(
			(page: any) =>
				page?.data &&
				page.data.map((user: UserDataType, index: number) => {
					return { label: user.name, value: user.userId };
				})
		);

		setUserOptions(newUsers.flat());

		if (hasNextUsersPage) {
			fetchNextUsersPage().then((r) => null);
		}
	};

	const {
		watch,
		control,
		register,
		handleSubmit,
		getValues,
		setValue,
		setError,
		clearErrors,
		reset,
		formState: { errors, isValid },
	} = formMethods;

	const postageFieldArrayName = props.existingData
		? `postageCountry${props.existingData.title.replace(/ /g, '_')}`
		: `postageCountry`;

	const [addAddressActive, setAddAddressActive] = useState<boolean>(false);
	const [createUserOpen, setCreateUserOpen] = useState<boolean>(false);

	const [importCsvOpen, setImportCsvOpen] = useState<boolean>(false);
	const [importVcfOpen, setImportVcfOpen] = useState<boolean>(false);

	const [uniqueCountries, setUniqueCountries] = useState<any>([]);

	const [useWindow, setUseWindow] = useState<boolean>(props.existingData?.addresses[0].id === 100);

	const [resetPage, setResetPage] = useState<boolean>(false);

	const [addressItemCounts, setAddressItemCounts] = useState<any>({ loaded: 0, total: 0 });
	const [usersItemCounts, setUsersItemCounts] = useState<any>({ loaded: 0, total: 0 });

	const useWindowRef = useRef<boolean>(useWindow);

	const ShapeData = useCallback(
		(data: FieldValues, preview?: boolean) => {
			let shapedData: SendsPageDataType = {
				title: data.title,
				description: data.description,
				defaultEmailAddress: data.defaultEmailAddress || undefined,
				defaultPhoneNumber:
					data.defaultPhoneNumber && data.defaultPhoneNumber !== '' ? data.defaultPhoneNumber : undefined,
				allowCreateAddress: data.allowCreateAddress ? data.allowCreateAddress : false,
				isActive: true,
				addresses: [],
				users: [],
			};

			let countries: Array<any> = [];
			if (useWindowRef.current) {
				const countryCodeMatchIndex = GetObjectIndexByKeyValue(data[postageFieldArrayName], 'label', 'GB');
				const envelopeAddress = {
					label: 'Windowed Envelope',
					value: 100,
					object: { addressId: 100, countryCode: 'GB', label: 'Windowed Envelope' },
				};

				shapedData.addresses.push({
					id: 100,
					postageOptions:
						countryCodeMatchIndex > -1 ? data[postageFieldArrayName][countryCodeMatchIndex].value : [],
				});

				countries.push(envelopeAddress);
			} else {
				addressData?.pages.map(
					(page: any) =>
						page?.data &&
						page.data.forEach((address: AddressListItemType) => {
							if (data.addresses && data.addresses.includes(address.object.addressId)) {
								countries.push(address);

								const countryCodeMatchIndex = GetObjectIndexByKeyValue(
									data[postageFieldArrayName],
									'label',
									address.object.countryCode
								);

								if (preview) {
									shapedData.addresses.push({
										id: address.object.addressId,
										value: address.label,
										postageOptions:
											countryCodeMatchIndex > -1
												? data[postageFieldArrayName][countryCodeMatchIndex].value
												: [],
									});
								} else {
									shapedData.addresses.push({
										id: address.object.addressId,
										postageOptions:
											countryCodeMatchIndex > -1
												? data[postageFieldArrayName][countryCodeMatchIndex].value
												: [],
									});
								}
							}
						})
				);
			}

			countries = Array.from(new Set(countries));
			setUniqueCountries(countries);

			data.users && data.users.forEach((user: string) => shapedData.users.push({ id: user }));

			return shapedData;
		},
		[addressData]
	);

	const HandleSave = (data: FieldValues) => {
		props.saveCallback(ShapeData(data));
	};

	const HandleNewUser = (id: number) => {
		const currentData = getValues('users') ?? [];
		const newData: any = [...currentData, id];
		setCreateUserOpen(false);
		setPreSelectedUsers(newData);
		setValue('users', newData);
	};

	const HandleNewAddress = (id: number) => {
		const currentData = getValues('addresses') ?? [];
		const newData: any = [...currentData, id];

		setAddAddressActive(false);
		setPreSelectedAddresses(newData);
		setValue('addresses', newData);
	};

	const HandleNewBulkAddresses = (data: Array<AddressListItemType>) => {
		const newIds: Array<number> = [];
		data.forEach((item: AddressListItemType) => newIds.push(item.value));

		const currentData = getValues('addresses') ?? [];
		const newData: any = [...currentData, ...newIds];

		setAddAddressActive(false);
		setPreSelectedAddresses(newData);
		setValue('addresses', newData);

		setImportCsvOpen(false);
		setImportVcfOpen(false);
	};

	const HandleFinalSaveDecision = () => {
		setActiveSendsPage({ isOpen: false, pageData: null });
		reset();
		setResetPage(true);
	};

	const UpdateUseWindow = (state: boolean) => {
		useWindowRef.current = state;
		setUseWindow(state);

		setValue('allowCreateAddress', !state ? props.existingData?.allowCreateAddress || false : false);

		if (state) {
			setPreSelectedAddresses([100]);
			setValue('addresses', [100]);
		} else {
			const existingAddresses = props.existingData?.addresses.map((item) => item.id);
			setPreSelectedAddresses(
				!existingAddresses || (existingAddresses && existingAddresses[0] === 100) ? [] : existingAddresses
			);
			setValue(
				'addresses',
				!existingAddresses || (existingAddresses && existingAddresses[0] === 100) ? [] : existingAddresses
			);
		}
	};

	useEffect(() => {
		setActiveSendsPage({ isOpen: !!props.saveResponse, pageData: props.saveResponse });
	}, [props.saveResponse]);

	useEffect(() => {
		if (addressData) {
			MapAddressOptions(addressData);
		}
		if (usersData) {
			MapUserOptions(usersData);
		}
	}, [addressData, usersData, addressDataUpdatedAt, usersDataUpdatedAt]);

	useEffect(() => {
		if (addressData && props.existingData) {
			ShapeData({ ...props.existingData, addresses: props.existingData.addresses.map((item) => item.id) });
		}
	}, [props, addressData, addressDataUpdatedAt]);

	useEffect(() => {
		const subscription = watch((e) => {
			if (addressData) {
				let newData = ShapeData(e);
				if (props.dataUpdateCallback) {
					props.dataUpdateCallback(newData);
				}
			}
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [props, addressLoading, addressData, watch]);

	useEffect(() => {
		document.title = props.existingData ? `Editing '${props.existingData?.title}'` : 'Creating Sends Profile';
	}, []);

	return (
		<>
			<FormProvider {...formMethods}>
				<form onSubmit={handleSubmit((data) => HandleSave(data))}>
					<fieldset className={'flex-column flex-gap-md'}>
						{errors && Object.values(errors).map((error) => error && <div>{error.message as string}</div>)}

						<FormWrapperLabel
							label={'Title'}
							value={getValues().title}
							invalid={errors.title ? true : null}
							validationMessage={errors.title?.message as string}
						>
							<input
								type='text'
								aria-invalid={errors.title ? 'true' : 'false'}
								defaultValue={props.existingData?.title}
								{...register('title', {
									required: 'This is a required field',
									minLength: { value: 2, message: 'Must be longer than 2 characters' },
									maxLength: { value: 512, message: 'Maximum 512 characters' },
								})}
							/>
						</FormWrapperLabel>

						<FormWrapperLabel
							label={'Description'}
							labelExtra={'(Optional)'}
							value={getValues().description}
							invalid={errors.description ? true : null}
							validationMessage={errors.description?.message as string}
						>
							<textarea
								rows={4}
								aria-invalid={errors.description ? 'true' : 'false'}
								defaultValue={props.existingData?.description}
								{...register('description', {
									minLength: { value: 2, message: 'Must be longer than 2 characters' },
									maxLength: { value: 1024, message: 'Maximum 1024 characters' },
								})}
							/>
						</FormWrapperLabel>

						<div>
							<label className={'mb-xs'}>Address Options</label>
							<div
								className={`${uniqueCountries.length > 0 ? 'p-sm' : ''}`}
								style={{
									borderRadius: 'var(--border-radius)',
									backgroundColor: uniqueCountries.length > 0 ? 'var(--surface-bg)' : '',
								}}
							>
								<StackVertical gap={FlexGap.ExtraSmall}>
									{canWindow ? (
										<div className={'btn-group btn-group-row'}>
											<Button
												title={'Use Addresses'}
												fullWidth={true}
												colour={useWindow ? ColourMod.Secondary : ColourMod.Success}
												onClick={() => UpdateUseWindow(false)}
											/>
											<Button
												title={'Use Envelope Window'}
												fullWidth={true}
												colour={useWindow ? ColourMod.Success : ColourMod.Secondary}
												onClick={() => UpdateUseWindow(true)}
											/>
										</div>
									) : (
										<div className={'mb-sm'}>
											<AlertBanner
												title={`We can offer Window Envelope options, <a href='https://www.ukpostbox.com/forms/sending-mail' target='_blank'>contact our Sends Team for a quote</a>`}
											/>
										</div>
									)}

									<StackVertical gap={FlexGap.ExtraSmall}>
										{!useWindow && (
											<InfiniteDataComboBox
												name={'addresses'}
												label={''}
												placeholder={'Select Address...'}
												data={AddressOptions}
												defaultValues={preSelectedAddresses}
												isLoading={addressLoading}
												valueType={'number'}
												isFetchingNextPage={isFetchingNextAddressPage}
												fetchNextPage={fetchNextAddressPage}
												hasNextPage={hasNextAddressPage}
												itemCounts={addressItemCounts}
												required={true}
												reset={resetPage}
											/>
										)}

										{!useWindow && (
											<div className={'row flex-gap-sm'}>
												<div className={'col-xs-12 col-sm-6'}>
													<Button
														title={'New Address'}
														disabled={addressLoading}
														size={ButtonSize.Small}
														fullWidth={true}
														onClick={() => setAddAddressActive(true)}
													/>
												</div>
												<div className={'col-xs-12 col-sm-6'}>
													<Button
														title={'Import from CSV'}
														disabled={addressLoading}
														size={ButtonSize.Small}
														fullWidth={true}
														onClick={() => setImportCsvOpen(true)}
													/>
												</div>
												{/*<div className={'col-xs-12 col-sm-12 col-md-4'}>
													<Button
														title={'Import from vCard'}
														disabled={addressLoading}
														size={ButtonSize.Small}
														fullWidth={true}
														onClick={() => setImportVcfOpen(true)}
													/>
												</div>*/}
											</div>
										)}

										<DeliveryOptionsByCountry
											postageFieldArrayName={postageFieldArrayName}
											existingData={props.existingData}
											uniqueCountries={uniqueCountries}
											reset={resetPage}
											usingEnvelopeWindow={useWindow}
										/>
									</StackVertical>
								</StackVertical>
							</div>
						</div>

						<InfiniteDataComboBox
							name={'users'}
							label={'Users'}
							placeholder={'Select Users...'}
							data={UserOptions}
							defaultValues={preSelectedUsers}
							isLoading={usersLoading}
							valueType={'string'}
							isFetchingNextPage={isFetchingNextUsersPage}
							fetchNextPage={fetchNextUsersPage}
							hasNextPage={hasNextUsersPage}
							itemCounts={usersItemCounts}
							required={true}
							reset={resetPage}
						/>

						<Button
							title={'New User'}
							disabled={usersLoading}
							fullWidth={true}
							size={ButtonSize.Small}
							onClick={() => setCreateUserOpen(true)}
						/>

						<div
							className={`can-create-address-container ${
								canCreateAddresses ? 'show-create-address-extras' : ''
							}`}
						>
							<Controller
								control={control}
								name='allowCreateAddress'
								defaultValue={props.existingData?.allowCreateAddress ?? false}
								render={({ field, fieldState: { error } }) => (
									<ToggleSwitch
										inForm={true}
										type={ToggleType.Switch}
										isChecked={field.value ?? props.existingData?.allowCreateAddress}
										onChange={(e) => {
											// @ts-ignore
											setCanCreateAddresses(e.target.checked);
											field.onChange(e);
										}}
										validationMessage={error?.message}
										disabled={useWindow}
										label={'Users can add new Addresses?'}
										name={'UsersAddAddress'}
									/>
								)}
							/>

							{canCreateAddresses && (
								<StackVertical gap={FlexGap.Medium} extraClass={`mt-md create-address-extras`}>
									<AlertBanner
										title={`Provide Default Contact Values`}
										content={
											"These values will be pre-filled when a user creates an address, but they'll still have the option to remove them and provide their own"
										}
									/>
									<EmailControl
										control={control}
										name={'defaultEmailAddress'}
										defaultValue={props.existingData?.defaultEmailAddress}
										label={'Default Email Address'}
										labelExtra={'(Optional)'}
										validationMessage={errors.defaultEmailAddress?.message as string}
										setError={setError}
										setValue={setValue}
										clearErrors={clearErrors}
									/>

									<TelephoneControl
										label={'Default Phone No'}
										labelExtra={'(Optional)'}
										name={'defaultPhoneNumber'}
										forwardStatus={(e: boolean) => setLookupActive(e)}
										defaultValue={props.existingData?.defaultPhoneNumber}
									/>
								</StackVertical>
							)}
						</div>

						{props.error &&
							Object.entries(props.error).map(([k, v], index) => (
								<AlertBanner title={props.error[k].toString()} colourMod={ColourMod.Alert} />
							))}

						<ButtonSubmit
							title={'Save Profile'}
							colour={ColourMod.Primary}
							fullWidth={true}
							loading={props.savingData}
							disabled={lookupActive || !isValid}
						/>
					</fieldset>
				</form>
			</FormProvider>

			{createUserOpen && (
				<CreateUserModal
					visible={createUserOpen}
					closeCallback={() => setCreateUserOpen(false)}
					actionedCallback={(id: number) => HandleNewUser(id)}
				/>
			)}

			{addAddressActive && (
				<AddAddressModal
					visible={addAddressActive}
					closeCallback={() => setAddAddressActive(false)}
					actionedCallback={(address: AddressType) => HandleNewAddress(address.addressId)}
					existingData={{
						phoneNo: getValues('defaultPhoneNumber'),
						emailAddress: getValues('defaultEmailAddress'),
					}}
				/>
			)}

			{importCsvOpen && (
				<ImportCsvModal
					visible={importCsvOpen}
					closeCallback={() => setImportCsvOpen(false)}
					actionedCallback={(e: any) => HandleNewBulkAddresses(e)}
				/>
			)}

			{importVcfOpen && (
				<ImportVcfModal
					visible={importVcfOpen}
					closeCallback={() => setImportVcfOpen(false)}
					actionedCallback={(e: any) => HandleNewBulkAddresses(e)}
				/>
			)}

			{activeSendsPage.isOpen && (
				<SendsPageCreatedActionsModal
					pageData={activeSendsPage.pageData}
					isOpen={activeSendsPage.isOpen}
					actionedCallback={() => HandleFinalSaveDecision()}
					closeCallback={() => setActiveSendsPage({ ...activeSendsPage, isOpen: false })}
				/>
			)}
		</>
	);
});

export default CreateStandaloneSendForm;

type postageFieldsType = {
	postageFieldArrayName: string;
	existingData?: SendsPageDataType;
	uniqueCountries?: any;
	reset?: boolean;
	usingEnvelopeWindow?: boolean;
};
const DeliveryOptionsByCountry = (props: postageFieldsType) => {
	const { control, getValues, unregister } = useFormContext();

	const { fields, append, remove } = useFieldArray({
		control,
		name: props.postageFieldArrayName,
		rules: { required: 'Postage Options are required' },
	});

	const BuildCountryPostage = useCallback(() => {
		const countryList: any = [];

		fields.forEach((item: any, index: number) => {
			const existingIndex = GetObjectIndexByKeyValue(props.uniqueCountries, 'object', item.label, 'countryCode');
			const existingIndex2 = countryList.indexOf(item.address?.countryCode);

			countryList.push(item.address?.countryCode);

			if (existingIndex === -1 || existingIndex2 !== -1) {
				remove(index);
			}
		});

		props.uniqueCountries.forEach((item: any, index: number) => {
			const existingIndex = GetObjectIndexByKeyValue(fields, 'label', item.object.countryCode);

			if (existingIndex === -1) {
				append({ label: item.object.countryCode, address: item.object });
			}
		});
	}, [append, fields, props.uniqueCountries, props.usingEnvelopeWindow, remove]);

	useEffect(() => {
		BuildCountryPostage();
	}, [props.uniqueCountries]);

	useEffect(() => {
		remove();
	}, [props.usingEnvelopeWindow]);

	return (
		<>
			{fields.map((address: any, index) => {
				return (
					<div key={`postageEntry${index}`}>
						{!props.usingEnvelopeWindow && <hr />}
						<SendsPostageControl
							key={`${props.usingEnvelopeWindow ? 'window' : 'address'}_${address.id}`}
							name={`${props.postageFieldArrayName}.${index}.value`}
							control={control}
							getValues={getValues}
							unregister={unregister}
							countryCode={address.label}
							label={'Postage'}
							reset={props.reset}
							defaultValues={
								props.existingData?.addresses?.filter(
									(item) => item.id === address.address?.addressId
								)[0]?.postageOptions
							}
						/>
					</div>
				);
			})}
		</>
	);
};
